import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
  forwardRef
} from 'react';
import { Modal, Form, Row, Col, Spinner } from 'react-bootstrap';
import Button from 'components/base/Button';
import ReactDatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { CREATE_JOB } from 'services/api/job';
import { GET_JOBSTAGE_QUERY, JobStage } from 'services/api/jobStage';
import { GET_EMPLOYEES_QUERY } from 'services/api/employee';
import { JobWithMetadata } from 'services/api/job';
import { SEARCH_JOB_TYPES, JobType } from 'services/api/jobTypes';
import { SEARCH_CUSTOMERS } from 'services/api/customer-rep';
import { SEARCH_RIGS, RigResponse } from 'services/api/rigs';
import { SEARCH_LOCATIONS, Location } from 'services/api/location';
import { GET_COMPANY_REPS } from 'services/api/customer';
import toast from 'react-hot-toast';
import {
  FormData,
  IndexKey,
  ObjectField
} from 'pages/apps/project-management/types/types';
import { isValidObjectId } from 'components/labels/Label';
import RequiredLabel from 'components/labels/Label';
import { toSentenceCase } from 'helpers/utils';
import { Renderable, Toast, ValueFunction } from 'react-hot-toast';
import { Employee, JobClient, ContactPerson } from 'data/users';
import { debounce } from 'lodash';
import dayjs from 'dayjs';

interface DuplicateJobModalProps {
  show: boolean;
  onHide: () => void;
  job: JobWithMetadata;
  onSuccess?: () => void;
}

type DateFieldName =
  | 'departureDate'
  | 'returnDate'
  | 'expectedEquipmentShipDate';

type DateInputProps = {
  value?: string;
  onClick?: React.MouseEventHandler<HTMLInputElement>;
  placeholder?: string;
  isInvalid?: boolean;
  name?: string;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  className?: string;
  style?: React.CSSProperties;
};

const DateInput = forwardRef<HTMLInputElement, DateInputProps>(
  ({ isInvalid, className, style, ...rest }, ref) => (
    <Form.Control
      {...rest}
      readOnly
      isInvalid={isInvalid}
      className={className}
      style={{ padding: '8px 45px', ...(style || {}) }}
      ref={ref}
    />
  )
);

DateInput.displayName = 'DateInput';

const DuplicateJobModal: React.FC<DuplicateJobModalProps> = ({
  show,
  onHide,
  job,
  onSuccess
}) => {
  const [validated, setValidated] = useState<boolean>(false);
  const [formData, setFormData] = useState<Partial<FormData>>({
    title: '',
    well: '',
    destinationType: '' as any,
    supervisor: [],
    customer: { _id: '' },
    customerRepresentative: { _id: '' },
    rig: { _id: '' },
    jobType: { _id: '' },
    location: { _id: '' },
    leavingFrom: '',
    jobNumber: '',
    expectedEquipmentShipDate: undefined,
    departureDate: undefined,
    returnDate: undefined,
    description: '',
    comment: '',
    jobStage: { _id: '' }
  });
  const [validationMessages, setValidationMessages] = useState<
    Record<string, string>
  >({});
  const queryClient = useQueryClient();

  const [enabledFields, setEnabledFields] = useState<Set<string>>(new Set());

  const [jobTypeSearchResults, setJobTypeSearchResults] = useState<JobType[]>(
    []
  );
  const [isJobTypeSearching, setIsJobTypeSearching] = useState<boolean>(false);
  const [jobTypeSearchTerm, setJobTypeSearchTerm] = useState<string>('');
  const [isJobTypeInputFocused, setIsJobTypeInputFocused] =
    useState<boolean>(false);
  const jobTypeRef = useRef<HTMLDivElement>(null);

  // Customer search states
  const [customerSearchResults, setCustomerSearchResults] = useState<
    JobClient[]
  >([]);
  const [isCustomerSearching, setIsCustomerSearching] =
    useState<boolean>(false);
  const [customerSearchTerm, setCustomerSearchTerm] = useState<string>('');
  const [isCustomerInputFocused, setIsCustomerInputFocused] =
    useState<boolean>(false);
  const customerRef = useRef<HTMLDivElement>(null);

  // Rig search states
  const [rigSearchResults, setRigSearchResults] = useState<RigResponse[]>([]);
  const [isRigSearching, setIsRigSearching] = useState<boolean>(false);
  const [rigSearchTerm, setRigSearchTerm] = useState<string>(
    job.rig?.name || ''
  );
  const [isRigInputFocused, setIsRigInputFocused] = useState<boolean>(false);
  const rigRef = useRef<HTMLDivElement>(null);

  // Location search states
  const [locationSearchResults, setLocationSearchResults] = useState<
    Location[]
  >([]);
  const [isLocationSearching, setIsLocationSearching] =
    useState<boolean>(false);
  const [locationSearchTerm, setLocationSearchTerm] = useState<string>(
    typeof job.location === 'string'
      ? job.location
      : job.location?.location || ''
  );
  const [isLocationInputFocused, setIsLocationInputFocused] =
    useState<boolean>(false);
  const locationRef = useRef<HTMLDivElement>(null);

  const getDateValue = useCallback((value: FormData['departureDate']) => {
    if (!value) {
      return null;
    }

    if (value instanceof Date) {
      return value;
    }

    if (Array.isArray(value)) {
      const firstValid = value.find(item => dayjs(item).isValid());
      return firstValid ? dayjs(firstValid).toDate() : null;
    }

    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.toDate() : null;
  }, []);

  const { data: jobStages } = useQuery('getJobStages', GET_JOBSTAGE_QUERY, {
    staleTime: 3 * 60 * 60 * 1000
  });

  const { data: employees } = useQuery(
    'get_all_employees',
    GET_EMPLOYEES_QUERY,
    {
      staleTime: 3 * 60 * 60 * 1000
    }
  );

  const customerIdForReps =
    (formData.customer as any)?.id || formData.customer?._id || '';
  const { data: customerReps } = useQuery(
    ['getCompanyReps', customerIdForReps],
    () => GET_COMPANY_REPS(customerIdForReps),
    {
      enabled: !!customerIdForReps,
      staleTime: 3 * 60 * 60 * 1000
    }
  );

  useEffect(() => {
    if (show && job) {
      try {
        const supervisorId =
          job.supervisor?.length > 0
            ? (job.supervisor[0] as any).id || job.supervisor[0]._id || ''
            : '';
        setFormData({
          title: job.title || '',
          well: job.well || '',
          destinationType: job.destinationType || ('' as any),
          supervisor: supervisorId ? [{ _id: supervisorId }] : [],
          customer: {
            _id: (job.customer as any)?.id || job.customer?._id || ''
          },
          customerRepresentative: {
            _id:
              (job.customerRepresentative as any)?.id ||
              job.customerRepresentative?._id ||
              ''
          },
          rig: { _id: (job.rig as any)?.id || job.rig?._id || '' },
          jobType: { _id: '' },
          location: {
            _id:
              typeof job.location === 'object'
                ? (job.location as any)?.id || (job.location as any)?._id || ''
                : ''
          },
          leavingFrom: job.leavingFrom || '',
          jobNumber: '',
          expectedEquipmentShipDate: undefined,
          departureDate: undefined,
          returnDate: undefined,
          description: '',
          comment: '',
          jobStage: { _id: '' }
        });
        setEnabledFields(new Set());
        setValidationMessages({});
        setValidated(false);
        setCustomerSearchTerm(job.customer?.organizationName || '');
        setRigSearchTerm(job.rig?.name || '');
        setLocationSearchTerm(
          typeof job.location === 'string'
            ? job.location
            : job.location?.location || ''
        );
      } catch (error) {
        console.error('Error initializing duplicate job form:', error);
        toast.error('Error loading job data. Please try again.');
      }
    }
  }, [show, job]);

  useEffect(() => {
    if (jobStages?.data && !formData.jobStage?._id && show) {
      const newJobStage = jobStages.data.find(
        (stage: JobStage) => stage.name.toLowerCase() === 'new job'
      );
      if (newJobStage) {
        setFormData(prev => ({
          ...prev,
          jobStage: { _id: newJobStage.id }
        }));
      }
    }
  }, [jobStages, formData.jobStage?._id, show]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const isDropdownClick =
        target.closest('.form-floating') ||
        target.closest('.dropdown-container');
      if (!isDropdownClick) {
        setIsJobTypeInputFocused(false);
        setJobTypeSearchResults([]);
        if (!(formData.jobType as any)?.id && !formData.jobType?._id) {
          setJobTypeSearchTerm('');
        }
        setIsCustomerInputFocused(false);
        setCustomerSearchResults([]);
        setIsRigInputFocused(false);
        setRigSearchResults([]);
        setIsLocationInputFocused(false);
        setLocationSearchResults([]);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [formData.jobType?._id]);

  const enableField = (fieldName: string) => {
    setEnabledFields(prev => new Set(prev).add(fieldName));
    setTimeout(() => {
      const element =
        document.getElementById(fieldName) ||
        document.querySelector(`[name="${fieldName}"]`) ||
        document.querySelector(`#${fieldName}`);
      if (element) {
        (element as HTMLElement).focus();
      }
    }, 50);
  };

  const debouncedJobTypeSearch = useCallback(
    debounce(async (query: string) => {
      if (query.length < 2) return;
      setIsJobTypeSearching(true);
      try {
        const response = await SEARCH_JOB_TYPES(query);
        setJobTypeSearchResults(response.data);
      } catch (error) {
        console.error('Error searching job types:', error);
        setJobTypeSearchResults([]);
      } finally {
        setIsJobTypeSearching(false);
      }
    }, 500),
    []
  );

  const debouncedCustomerSearch = useCallback(
    debounce(async (query: string) => {
      if (query.length < 2) return;
      setIsCustomerSearching(true);
      try {
        const response = await SEARCH_CUSTOMERS(query);
        setCustomerSearchResults(response.data);
      } catch (error) {
        console.error('Error searching customers:', error);
        setCustomerSearchResults([]);
      } finally {
        setIsCustomerSearching(false);
      }
    }, 500),
    []
  );

  const debouncedRigSearch = useCallback(
    debounce(async (query: string) => {
      if (query.length < 2) return;
      setIsRigSearching(true);
      try {
        const { data } = await SEARCH_RIGS(query);
        setRigSearchResults(data);
      } catch (error) {
        console.error('Error searching rigs:', error);
        setRigSearchResults([]);
      } finally {
        setIsRigSearching(false);
      }
    }, 500),
    []
  );

  const debouncedLocationSearch = useCallback(
    debounce(async (query: string) => {
      if (query.length < 2) return;
      setIsLocationSearching(true);
      try {
        const { data } = await SEARCH_LOCATIONS(query);
        setLocationSearchResults(data);
      } catch (error) {
        console.error('Error searching locations:', error);
        setLocationSearchResults([]);
      } finally {
        setIsLocationSearching(false);
      }
    }, 500),
    []
  );

  const handleJobTypeSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setJobTypeSearchTerm(value);
    if (value.length >= 2) {
      debouncedJobTypeSearch(value);
    } else {
      setJobTypeSearchResults([]);
    }
  };

  const handleCustomerSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCustomerSearchTerm(value);
    if (value.length >= 2) {
      debouncedCustomerSearch(value);
    } else {
      setCustomerSearchResults([]);
    }
  };

  const handleRigSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setRigSearchTerm(value);
    if (value.length >= 2) {
      debouncedRigSearch(value);
    } else {
      setRigSearchResults([]);
    }
  };

  const handleLocationSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLocationSearchTerm(value);
    if (value.length >= 2) {
      debouncedLocationSearch(value);
    } else {
      setLocationSearchResults([]);
    }
  };

  const createJobMutation = useMutation('createJobMutation', CREATE_JOB, {
    onSuccess: () => {
      queryClient.invalidateQueries('getJobs');
      toast.success('Job duplicated successfully');
      onHide();
      if (onSuccess) {
        onSuccess();
      }
    },
    onError: (error: any): any => {
      if (error?.response?.data.message) {
        return toast.error(error?.response?.data.message);
      } else {
        error?.response?.data.messages?.map(
          (message: Renderable | ValueFunction<Renderable, Toast>) =>
            toast.error(toSentenceCase(message as string))
        );
      }
    }
  });

  const validateForm = useCallback(() => {
    const newErrors: Partial<Record<keyof FormData, string>> = {};
    let isValid = true;

    const requiredFieldsToCheck = [
      'well',
      'jobNumber',
      'destinationType',
      'supervisor',
      'expectedEquipmentShipDate',
      'customer',
      'jobType',
      'jobStage',
      'location'
    ];

    requiredFieldsToCheck.forEach(field => {
      const value = formData[field as keyof FormData];

      if (ObjectField.includes(field)) {
        if (field === 'supervisor') {
          const supervisors = value as IndexKey[];
          if (!supervisors?.[0]?._id || !isValidObjectId(supervisors[0]._id)) {
            isValid = false;
            newErrors[field] = `${
              field.charAt(0).toUpperCase() + field.slice(1)
            } is required`;
          }
        } else {
          const indexKeyValue = value as IndexKey;
          if (!indexKeyValue?._id || !isValidObjectId(indexKeyValue._id)) {
            isValid = false;
            newErrors[field as keyof FormData] = `${
              field.charAt(0).toUpperCase() + field.slice(1)
            } is required`;
          }
        }
      } else {
        if (!value || (typeof value === 'string' && value.trim() === '')) {
          isValid = false;
          newErrors[field as keyof FormData] = `${
            field.charAt(0).toUpperCase() + field.slice(1)
          } is required`;
        }
      }
    });

    setValidationMessages(newErrors);
    setValidated(true);
    return isValid;
  }, [formData]);

  const handleInputChange = useCallback(
    (
      e: React.ChangeEvent<
        HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
      >
    ) => {
      const { name, value } = e.target;

      if (!ObjectField.includes(name)) {
        setFormData(prevData => ({ ...prevData, [name]: value }));
      } else {
        if (name === 'supervisor') {
          setFormData(prevData => ({
            ...prevData,
            [name]: [{ _id: value }]
          }));
        } else {
          setFormData(prevData => ({
            ...prevData,
            [name]: { _id: value }
          }));
        }
      }

      setValidationMessages(prev => ({ ...prev, [name]: '' }));
    },
    []
  );

  const handleDateChange = useCallback(
    (value: Date | undefined, fieldName: DateFieldName) => {
      const normalizedValue = value ? new Date(value) : undefined;

      setFormData(prev => ({
        ...prev,
        [fieldName]: normalizedValue
      }));

      setValidationMessages(prev => ({ ...prev, [fieldName]: '' }));
    },
    []
  );

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (validateForm()) {
        let expectedEquipmentShipDateValue = formData.expectedEquipmentShipDate;
        if (expectedEquipmentShipDateValue instanceof Date) {
          expectedEquipmentShipDateValue =
            expectedEquipmentShipDateValue.toISOString();
        }

        let departureDateValue = formData.departureDate;
        if (departureDateValue instanceof Date) {
          departureDateValue = departureDateValue.toISOString();
        }

        let returnDateValue = formData.returnDate;
        if (returnDateValue instanceof Date) {
          returnDateValue = returnDateValue.toISOString();
        }

        const submitData: FormData = {
          ...formData,
          well: formData.well || '',
          jobNumber: formData.jobNumber || '',
          destinationType: formData.destinationType || ('' as any),
          supervisor: formData.supervisor || [],
          expectedEquipmentShipDate: expectedEquipmentShipDateValue || '',
          customer: formData.customer || { _id: '' },
          customerRepresentative: formData.customerRepresentative || {
            _id: ''
          },
          jobType: formData.jobType || { _id: '' },
          jobStage: formData.jobStage || { _id: '' },
          location: formData.location || { _id: '' },
          title: formData.title || '',
          leavingFrom: formData.leavingFrom || '',
          rig: formData.rig || { _id: '' },
          description: formData.description || '',
          departureDate: departureDateValue,
          returnDate: returnDateValue,
          comment: formData.comment || ''
        } as FormData;
        createJobMutation.mutate(submitData);
      }
    },
    [formData, validateForm, createJobMutation]
  );

  return (
    <>
      <style>
        {`
          .dark .duplicate-field-copied {
            background-color: var(--phoenix-gray-200, #31374a) !important;
          }
          .dark .duplicate-field-required {
            background-color: var(--phoenix-white, #fff) !important;
          }
        `}
      </style>
      <Modal show={show} onHide={onHide} size="lg" scrollable>
        <Modal.Header closeButton>
          <Modal.Title>Duplicate Job</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form noValidate validated={validated} onSubmit={handleSubmit}>
            <Row className="g-3">
              {/* Job Title - Editable when clicked */}
              <Col sm={12}>
                <Form.Group controlId="title">
                  <Form.Label>Job Title</Form.Label>
                  {!enabledFields.has('title') ? (
                    <div
                      onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        enableField('title');
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      <Form.Control
                        type="text"
                        value={formData.title}
                        disabled
                        readOnly
                        style={{
                          cursor: 'pointer',
                          backgroundColor: '#f8f9fa',
                          pointerEvents: 'none'
                        }}
                        className="duplicate-field-copied"
                      />
                    </div>
                  ) : (
                    <Form.Control
                      type="text"
                      value={formData.title}
                      onChange={handleInputChange}
                      name="title"
                      id="title"
                      style={{
                        border: '2px solid var(--phoenix-primary, #3874ff)'
                      }}
                    />
                  )}
                  <Form.Text className="text-muted">
                    {enabledFields.has('title')
                      ? 'Click to edit'
                      : 'Copied from original job - Click to edit'}
                  </Form.Text>
                </Form.Group>
              </Col>

              {/* Well - Editable when clicked */}
              <Col sm={6}>
                <Form.Group controlId="well">
                  <Form.Label>Well</Form.Label>
                  {!enabledFields.has('well') ? (
                    <div
                      onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        enableField('well');
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      <Form.Control
                        type="text"
                        value={formData.well || ''}
                        disabled
                        readOnly
                        style={{
                          cursor: 'pointer',
                          backgroundColor: '#f8f9fa',
                          pointerEvents: 'none'
                        }}
                        className="duplicate-field-copied"
                      />
                    </div>
                  ) : (
                    <Form.Control
                      type="text"
                      value={formData.well || ''}
                      onChange={handleInputChange}
                      name="well"
                      id="well"
                      style={{
                        border: '2px solid var(--phoenix-primary, #3874ff)'
                      }}
                    />
                  )}
                  <Form.Text className="text-muted">
                    {enabledFields.has('well')
                      ? 'Click to edit'
                      : 'Copied from original job - Click to edit'}
                  </Form.Text>
                </Form.Group>
              </Col>

              {/* Job Number - Required */}
              <Col sm={6}>
                <Form.Group controlId="jobNumber">
                  <Form.Label>
                    <RequiredLabel>Job Number</RequiredLabel>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Job Number"
                    name="jobNumber"
                    value={formData.jobNumber || ''}
                    onChange={handleInputChange}
                    isInvalid={validated && !formData.jobNumber}
                    required
                    className="bg-white"
                    style={{ backgroundColor: 'var(--phoenix-white, #fff)' }}
                  />
                  <Form.Control.Feedback type="invalid">
                    {validationMessages.jobNumber ||
                      'Please enter a job number.'}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              {/* Destination Type - Editable when clicked */}
              <Col sm={6}>
                <Form.Group controlId="destinationType">
                  <Form.Label>Destination Type</Form.Label>
                  {enabledFields.has('destinationType') ? (
                    <Form.Select
                      name="destinationType"
                      value={formData.destinationType || ''}
                      onChange={handleInputChange}
                      id="destinationType"
                      style={{
                        border: '2px solid var(--phoenix-primary, #3874ff)'
                      }}
                    >
                      <option value="">Select Destination Type</option>
                      <option value="Domestic">Domestic</option>
                      <option value="International">International</option>
                    </Form.Select>
                  ) : (
                    <div
                      onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        enableField('destinationType');
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      <Form.Control
                        type="text"
                        value={formData.destinationType || ''}
                        disabled
                        readOnly
                        className="duplicate-field-copied"
                        style={{
                          cursor: 'pointer',
                          backgroundColor: '#f8f9fa',
                          pointerEvents: 'none'
                        }}
                      />
                    </div>
                  )}
                  <Form.Text className="text-muted">
                    {enabledFields.has('destinationType')
                      ? 'Click to edit'
                      : 'Copied from original job - Click to edit'}
                  </Form.Text>
                </Form.Group>
              </Col>

              {/* Job Lead/Supervisor - Editable when clicked */}
              <Col sm={6}>
                <Form.Group controlId="supervisor">
                  <Form.Label>Job Lead</Form.Label>
                  {enabledFields.has('supervisor') ? (
                    <Form.Select
                      name="supervisor"
                      value={formData.supervisor?.[0]?._id || ''}
                      onChange={handleInputChange}
                      id="supervisor"
                      style={{
                        border: '2px solid var(--phoenix-primary, #3874ff)'
                      }}
                    >
                      <option value="">Select Supervisor</option>
                      {employees?.data
                        ?.sort((a: Employee, b: Employee) =>
                          a.firstName.localeCompare(b.firstName)
                        )
                        ?.map((user: Employee) => {
                          const userId = (user as any).id || user._id;
                          return (
                            <option key={userId} value={userId}>
                              {user.firstName} {user.lastName}
                            </option>
                          );
                        })}
                    </Form.Select>
                  ) : (
                    <div
                      onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        enableField('supervisor');
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      <Form.Control
                        type="text"
                        value={
                          formData.supervisor?.[0] && employees?.data
                            ? (() => {
                                const supId = formData.supervisor?.[0]?._id;
                                const supervisor = employees.data.find(
                                  (emp: Employee) =>
                                    ((emp as any).id || emp._id) === supId
                                );
                                return supervisor
                                  ? `${supervisor.firstName} ${supervisor.lastName}`
                                  : 'N/A';
                              })()
                            : 'N/A'
                        }
                        disabled
                        readOnly
                        className="duplicate-field-copied"
                        style={{
                          cursor: 'pointer',
                          backgroundColor: '#f8f9fa',
                          pointerEvents: 'none'
                        }}
                      />
                    </div>
                  )}
                  <Form.Text className="text-muted">
                    {enabledFields.has('supervisor')
                      ? 'Click to edit'
                      : 'Copied from original job - Click to edit'}
                  </Form.Text>
                </Form.Group>
              </Col>

              {/* Expected Operator Departure Date - Not copied, always editable */}
              <Col sm={6}>
                <Form.Group controlId="departureDate">
                  <Form.Label>Expected Operator Departure Date</Form.Label>
                  {show && (
                    <ReactDatePicker
                      selected={getDateValue(formData.departureDate)}
                      onChange={date =>
                        handleDateChange(date ?? undefined, 'departureDate')
                      }
                      dateFormat="MMM d, yyyy"
                      customInput={
                        <DateInput
                          placeholder="Departure Date"
                          isInvalid={!!validationMessages.datePicker}
                          name="departureDate"
                          className="w-100"
                        />
                      }
                      wrapperClassName="w-100"
                    />
                  )}
                  <Form.Text className="text-muted">
                    Not copied from original job - Enter date if needed
                  </Form.Text>
                </Form.Group>
              </Col>

              {/* Expected Operator Return Date - Not copied, always editable */}
              <Col sm={6}>
                <Form.Group controlId="returnDate">
                  <Form.Label>Expected Operator Return Date</Form.Label>
                  {show && (
                    <ReactDatePicker
                      selected={getDateValue(formData.returnDate)}
                      onChange={date =>
                        handleDateChange(date ?? undefined, 'returnDate')
                      }
                      dateFormat="MMM d, yyyy"
                      customInput={
                        <DateInput
                          placeholder="Return Date"
                          name="returnDate"
                          className="w-100"
                        />
                      }
                      wrapperClassName="w-100"
                    />
                  )}
                  <Form.Text className="text-muted">
                    Not copied from original job - Enter date if needed
                  </Form.Text>
                </Form.Group>
              </Col>

              {/* Expected Equipment Ship Date - Required */}
              <Col sm={6}>
                <Form.Group controlId="expectedEquipmentShipDate">
                  <Form.Label>
                    <RequiredLabel>Expected Equipment Ship Date</RequiredLabel>
                  </Form.Label>
                  {show && (
                    <ReactDatePicker
                      selected={getDateValue(
                        formData.expectedEquipmentShipDate
                      )}
                      onChange={date =>
                        handleDateChange(
                          date ?? undefined,
                          'expectedEquipmentShipDate'
                        )
                      }
                      dateFormat="MMM d, yyyy"
                      customInput={
                        <DateInput
                          placeholder="Expected Equipment Ship Date"
                          isInvalid={
                            validated && !formData.expectedEquipmentShipDate
                          }
                          name="expectedEquipmentShipDate"
                          className="duplicate-field-required w-100"
                          style={{
                            backgroundColor: 'var(--phoenix-white, #fff)'
                          }}
                        />
                      }
                      wrapperClassName="w-100"
                    />
                  )}
                  <Form.Control.Feedback type="invalid">
                    {validationMessages.expectedEquipmentShipDate ||
                      'Please enter an Equipment Ship Date.'}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              {/* Customer - Editable when clicked */}
              <Col sm={6}>
                <Form.Group controlId="customer">
                  <Form.Label>Customer</Form.Label>
                  {enabledFields.has('customer') ? (
                    <div className="dropdown-container" ref={customerRef}>
                      <Form.Control
                        type="text"
                        placeholder="Search company..."
                        value={customerSearchTerm}
                        onChange={handleCustomerSearch}
                        onFocus={() => setIsCustomerInputFocused(true)}
                        isInvalid={!!validationMessages.customer}
                        id="customer"
                        style={{
                          border: '2px solid var(--phoenix-primary, #3874ff)'
                        }}
                      />
                      {isCustomerSearching && (
                        <Spinner
                          animation="border"
                          size="sm"
                          className="position-absolute"
                          style={{ right: '10px', top: '15px' }}
                        />
                      )}
                      {(isCustomerInputFocused ||
                        customerSearchResults?.length > 0) && (
                        <div
                          className="position-absolute w-100 mt-1 bg-white border rounded shadow-sm"
                          style={{ zIndex: 1000 }}
                        >
                          {customerSearchResults?.map(company => {
                            const companyId =
                              (company as any).id || company._id;
                            return (
                              <div
                                key={companyId}
                                className="px-2 py-1 cursor-pointer hover:bg-gray-100"
                                onClick={() => {
                                  const repId = company.customerRepresentative
                                    ? (company.customerRepresentative as any)
                                        .id ||
                                      company.customerRepresentative._id ||
                                      ''
                                    : '';
                                  setFormData(prev => ({
                                    ...prev,
                                    customer: { _id: companyId },
                                    title: company.organizationName,
                                    customerRepresentative: { _id: repId }
                                  }));
                                  setCustomerSearchTerm(
                                    company.organizationName
                                  );
                                  setCustomerSearchResults([]);
                                  setIsCustomerInputFocused(false);
                                }}
                              >
                                {company.organizationName}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div
                      onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        enableField('customer');
                        setCustomerSearchTerm(
                          job.customer?.organizationName || ''
                        );
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      <Form.Control
                        type="text"
                        value={job.customer?.organizationName || ''}
                        disabled
                        readOnly
                        className="duplicate-field-copied"
                        style={{
                          cursor: 'pointer',
                          backgroundColor: '#f8f9fa',
                          pointerEvents: 'none'
                        }}
                      />
                    </div>
                  )}
                  <Form.Text className="text-muted">
                    {enabledFields.has('customer')
                      ? 'Search and select customer'
                      : 'Copied from original job - Click to edit'}
                  </Form.Text>
                </Form.Group>
              </Col>

              {/* Customer Representative - Editable when clicked */}
              <Col sm={6}>
                <Form.Group controlId="customerRepresentative">
                  <Form.Label>Customer Rep</Form.Label>
                  {enabledFields.has('customerRepresentative') ? (
                    <Form.Select
                      name="customerRepresentative"
                      value={formData.customerRepresentative?._id || ''}
                      onChange={handleInputChange}
                      disabled={!formData.customer?._id}
                      id="customerRepresentative"
                      style={{
                        border: '2px solid var(--phoenix-primary, #3874ff)'
                      }}
                    >
                      <option value="">Select Customer Representative</option>
                      {customerReps?.data?.map((rep: ContactPerson) => {
                        const repId = (rep as any).id || rep._id;
                        return (
                          <option key={repId} value={repId}>
                            {rep.firstName} {rep.lastName}
                          </option>
                        );
                      })}
                    </Form.Select>
                  ) : (
                    <div
                      onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        enableField('customerRepresentative');
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      <Form.Control
                        type="text"
                        value={
                          job.customerRepresentative
                            ? `${job.customerRepresentative.firstName || ''} ${
                                job.customerRepresentative.lastName || ''
                              }`.trim()
                            : ''
                        }
                        disabled
                        readOnly
                        className="duplicate-field-copied"
                        style={{
                          cursor: 'pointer',
                          backgroundColor: '#f8f9fa',
                          pointerEvents: 'none'
                        }}
                      />
                    </div>
                  )}
                  <Form.Text className="text-muted">
                    {enabledFields.has('customerRepresentative')
                      ? 'Select customer representative'
                      : 'Copied from original job - Click to edit'}
                  </Form.Text>
                </Form.Group>
              </Col>

              {/* Rig - Editable when clicked */}
              <Col sm={6}>
                <Form.Group controlId="rig">
                  <Form.Label>Rig</Form.Label>
                  {enabledFields.has('rig') ? (
                    <div className="dropdown-container" ref={rigRef}>
                      <Form.Control
                        type="text"
                        placeholder="Search rig..."
                        value={rigSearchTerm}
                        onChange={handleRigSearch}
                        onFocus={() => setIsRigInputFocused(true)}
                        isInvalid={!!validationMessages.rig}
                        id="rig"
                        style={{
                          border: '2px solid var(--phoenix-primary, #3874ff)'
                        }}
                      />
                      {isRigSearching && (
                        <Spinner
                          animation="border"
                          size="sm"
                          className="position-absolute"
                          style={{ right: '10px', top: '15px' }}
                        />
                      )}
                      {(isRigInputFocused || rigSearchResults?.length > 0) && (
                        <div
                          className="position-absolute w-100 mt-1 bg-white border rounded shadow-sm"
                          style={{ zIndex: 1000 }}
                        >
                          {rigSearchResults?.map(rig => {
                            const rigId = (rig as any).id || rig._id;
                            return (
                              <div
                                key={rigId}
                                className="px-2 py-1 cursor-pointer hover:bg-gray-100"
                                onClick={() => {
                                  setFormData(prev => ({
                                    ...prev,
                                    rig: { _id: rigId }
                                  }));
                                  setRigSearchTerm(rig.name);
                                  setRigSearchResults([]);
                                  setIsRigInputFocused(false);
                                }}
                              >
                                {rig.name}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div
                      onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        enableField('rig');
                        setRigSearchTerm(job.rig?.name || '');
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      <Form.Control
                        type="text"
                        value={job.rig?.name || ''}
                        disabled
                        readOnly
                        className="duplicate-field-copied"
                        style={{
                          cursor: 'pointer',
                          backgroundColor: '#f8f9fa',
                          pointerEvents: 'none'
                        }}
                      />
                    </div>
                  )}
                  <Form.Text className="text-muted">
                    {enabledFields.has('rig')
                      ? 'Search and select rig'
                      : 'Copied from original job - Click to edit'}
                  </Form.Text>
                </Form.Group>
              </Col>

              {/* Job Type - Required */}
              <Col sm={6}>
                <Form.Group controlId="jobType">
                  <Form.Label>
                    <RequiredLabel>Job Type</RequiredLabel>
                  </Form.Label>
                  <div className="dropdown-container" ref={jobTypeRef}>
                    <Form.Control
                      type="text"
                      placeholder="Search job type..."
                      value={jobTypeSearchTerm}
                      onChange={handleJobTypeSearch}
                      onFocus={() => setIsJobTypeInputFocused(true)}
                      isInvalid={!!validationMessages.jobType}
                      className="duplicate-field-required"
                      style={{ backgroundColor: 'var(--phoenix-white, #fff)' }}
                    />
                    {isJobTypeSearching && (
                      <Spinner
                        animation="border"
                        size="sm"
                        className="position-absolute"
                        style={{ right: '10px', top: '15px' }}
                      />
                    )}
                    {(isJobTypeInputFocused ||
                      jobTypeSearchResults?.length > 0) && (
                      <div
                        className="position-absolute w-100 mt-1 bg-white border rounded shadow-sm"
                        style={{ zIndex: 1000 }}
                      >
                        {jobTypeSearchResults?.length > 0 && (
                          <div className="border-top my-2"></div>
                        )}
                        {jobTypeSearchResults?.map(jobType => {
                          const jobTypeId =
                            (jobType as any).id || jobType._id || '';
                          return (
                            <div
                              key={jobTypeId}
                              className="px-2 py-1 cursor-pointer hover:bg-gray-100"
                              onClick={() => {
                                setFormData(prev => ({
                                  ...prev,
                                  jobType: { _id: jobTypeId }
                                }));
                                setJobTypeSearchTerm(jobType.name);
                                setJobTypeSearchResults([]);
                                setIsJobTypeInputFocused(false);
                                setValidationMessages(prev => ({
                                  ...prev,
                                  jobType: ''
                                }));
                              }}
                            >
                              {jobType.name}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    <Form.Control.Feedback type="invalid">
                      {validationMessages.jobType ||
                        'Please select a Job Type.'}
                    </Form.Control.Feedback>
                  </div>
                </Form.Group>
              </Col>

              {/* Job Stage - Editable when clicked */}
              <Col sm={6}>
                <Form.Group controlId="jobStage">
                  <Form.Label>Job Stage</Form.Label>
                  {enabledFields.has('jobStage') ? (
                    <Form.Select
                      name="jobStage"
                      value={formData.jobStage?._id || ''}
                      onChange={handleInputChange}
                      id="jobStage"
                      style={{
                        border: '2px solid var(--phoenix-primary, #3874ff)'
                      }}
                    >
                      <option value="">Select Job Stage</option>
                      {jobStages?.data?.map((stage: JobStage) => (
                        <option key={stage.id} value={stage.id}>
                          {stage.name.trim()}
                        </option>
                      ))}
                    </Form.Select>
                  ) : (
                    <div
                      onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        enableField('jobStage');
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      <Form.Control
                        type="text"
                        value={
                          jobStages?.data?.find(
                            (stage: JobStage) =>
                              stage.id === formData.jobStage?._id
                          )?.name || 'New Job'
                        }
                        disabled
                        readOnly
                        className="duplicate-field-copied"
                        style={{
                          cursor: 'pointer',
                          backgroundColor: '#f8f9fa',
                          pointerEvents: 'none'
                        }}
                      />
                    </div>
                  )}
                  <Form.Text className="text-muted">
                    {enabledFields.has('jobStage')
                      ? 'Select job stage'
                      : 'Defaults to "New Job" - Click to edit'}
                  </Form.Text>
                </Form.Group>
              </Col>

              {/* Location - Editable when clicked */}
              <Col sm={6}>
                <Form.Group controlId="location">
                  <Form.Label>Location</Form.Label>
                  {enabledFields.has('location') ? (
                    <div className="dropdown-container" ref={locationRef}>
                      <Form.Control
                        type="text"
                        placeholder="Search location..."
                        value={locationSearchTerm}
                        onChange={handleLocationSearch}
                        onFocus={() => setIsLocationInputFocused(true)}
                        isInvalid={!!validationMessages.location}
                        id="location"
                        style={{
                          border: '2px solid var(--phoenix-primary, #3874ff)'
                        }}
                      />
                      {isLocationSearching && (
                        <Spinner
                          animation="border"
                          size="sm"
                          className="position-absolute"
                          style={{ right: '10px', top: '15px' }}
                        />
                      )}
                      {(isLocationInputFocused ||
                        locationSearchResults?.length > 0) && (
                        <div
                          className="position-absolute w-100 mt-1 bg-white border rounded shadow-sm"
                          style={{ zIndex: 1000 }}
                        >
                          {locationSearchResults?.map(location => {
                            const locationId =
                              (location as any).id || location._id;
                            return (
                              <div
                                key={locationId}
                                className="px-2 py-1 cursor-pointer hover:bg-gray-100"
                                onClick={() => {
                                  setFormData(prev => ({
                                    ...prev,
                                    location: { _id: locationId }
                                  }));
                                  setLocationSearchTerm(location.location);
                                  setLocationSearchResults([]);
                                  setIsLocationInputFocused(false);
                                }}
                              >
                                {location.location}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div
                      onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        enableField('location');
                        setLocationSearchTerm(
                          typeof job.location === 'string'
                            ? job.location
                            : job.location?.location || ''
                        );
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      <Form.Control
                        type="text"
                        value={
                          typeof job.location === 'string'
                            ? job.location
                            : job.location?.location || ''
                        }
                        disabled
                        readOnly
                        className="duplicate-field-copied"
                        style={{
                          cursor: 'pointer',
                          backgroundColor: '#f8f9fa',
                          pointerEvents: 'none'
                        }}
                      />
                    </div>
                  )}
                  <Form.Text className="text-muted">
                    {enabledFields.has('location')
                      ? 'Search and select location'
                      : 'Copied from original job - Click to edit'}
                  </Form.Text>
                </Form.Group>
              </Col>

              {/* Leaving From - Editable when clicked */}
              <Col sm={6}>
                <Form.Group controlId="leavingFrom">
                  <Form.Label>Leaving From</Form.Label>
                  {!enabledFields.has('leavingFrom') ? (
                    <div
                      onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        enableField('leavingFrom');
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      <Form.Control
                        type="text"
                        value={formData.leavingFrom || ''}
                        disabled
                        readOnly
                        style={{
                          cursor: 'pointer',
                          backgroundColor: '#f8f9fa',
                          pointerEvents: 'none'
                        }}
                        className="duplicate-field-copied"
                      />
                    </div>
                  ) : (
                    <Form.Control
                      type="text"
                      value={formData.leavingFrom || ''}
                      onChange={handleInputChange}
                      name="leavingFrom"
                      id="leavingFrom"
                      style={{
                        border: '2px solid var(--phoenix-primary, #3874ff)'
                      }}
                    />
                  )}
                  <Form.Text className="text-muted">
                    {enabledFields.has('leavingFrom')
                      ? 'Click to edit'
                      : 'Copied from original job - Click to edit'}
                  </Form.Text>
                </Form.Group>
              </Col>

              {/* Job Description - Editable when clicked */}
              <Col sm={12}>
                <Form.Group controlId="description">
                  <Form.Label>Job Description</Form.Label>
                  {!enabledFields.has('description') ? (
                    <div
                      onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        enableField('description');
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      <Form.Control
                        as="textarea"
                        value={formData.description || ''}
                        disabled
                        readOnly
                        style={{
                          cursor: 'pointer',
                          backgroundColor: '#f8f9fa',
                          height: '100px',
                          pointerEvents: 'none'
                        }}
                        className="duplicate-field-copied"
                      />
                    </div>
                  ) : (
                    <Form.Control
                      as="textarea"
                      value={formData.description || ''}
                      onChange={handleInputChange}
                      name="description"
                      id="description"
                      style={{
                        height: '100px',
                        border: '2px solid var(--phoenix-primary, #3874ff)'
                      }}
                    />
                  )}
                  <Form.Text className="text-muted">
                    {enabledFields.has('description')
                      ? 'Click to edit'
                      : 'Copied from original job - Click to edit'}
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={createJobMutation.isLoading}
            type="submit"
          >
            {createJobMutation.isLoading ? (
              <>
                <Spinner size="sm" className="me-2" animation="border" />
                Creating...
              </>
            ) : (
              'Duplicate Job'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default DuplicateJobModal;
